import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ApiError } from "../../../models/ApiResponse";
import { SalaryBatch } from "../../../models/Salary";
import AppIcon from "../../Core/AppIcon/AppIcon";
import AppText from "../../Core/AppText/AppText";
import Button from "../../Core/Button/Button";
import Modal from "../../Core/Modal/Modal";
import Flex from "../../Layout/Flex";
import LoadingIndicator from "../LoadingIndicator/LoadingIndicator";

interface Props {
	salaryBatch?: SalaryBatch;
	error?: ApiError;
	onClose?: () => void;
}

/**
 * Modal shown when salary batch is processing.
 *
 * Will show in different states depending on if salary batch is
 * processing, processed or failed.
 */
const SalaryBatchProcessingModal: React.FC<Props> = ({ salaryBatch, error, onClose }) => {
	const { t } = useTranslation();

	const [showLog, setShowLog] = useState(false);
	const [showCreatedEmployees, setShowCreatedCustomers] = useState(false);
	const [showUpdatedCustomers, setShowUpdatedCustomers] = useState(false);
	const [showProcessingWarnings, setShowProcessingWarnings] = useState(false);

	function getErrorDescription() {
		if (error) {
			return `${error.detail} (id ${error.id})`;
		}
		return "";
	}

	function showDiff(update: any, prev: any) {
		const updates = Object.keys(update).map(
			(k) => k + ": " + JSON.stringify(prev[k]) + " -> " + JSON.stringify(update[k])
		);
		window.alert(updates.join("\n"));
	}

	const processingLog = salaryBatch?.processingResult?.log || [];
	const processingWarnings = salaryBatch?.processingResult?.processingWarnings || [];
	const createdEmployees = salaryBatch?.processingResult?.createdEmployees || [];
	const updatedEmployees = salaryBatch?.processingResult?.updatedEmployees || [];

	const showCloseBtn =
		error || ["PROCESSED", "FAILED", "ROLLED_BACK"].includes(salaryBatch?.status as string);
	const showLoading =
		!error && (!salaryBatch || ["INITIAL", "PROCESSING"].includes(salaryBatch.status));

	return (
		<Modal maxWidth="650px" centerContent={false} onClose={onClose}>
			<Styles>
				{showLoading && (
					<>
						<LoadingIndicator showText={t("salaryBatch.processingModal.loading") as string} />
						<AppText textAlign="center" margin="1rem 0">
							{t("salaryBatch.processingModal.loadingInfo")}
						</AppText>
					</>
				)}

				{(error || salaryBatch?.status === "FAILED") && (
					<>
						<AppText textAlign="center" size="l" margin="1rem 0" color="danger" weight="medium">
							{t("salaryBatch.processingModal.failure")}
						</AppText>
						<AppText textAlign="center" margin="1rem 0" color="danger" size="xs">
							{getErrorDescription()}
						</AppText>
					</>
				)}

				{salaryBatch?.status === "PROCESSED" && (
					<Flex alignItems="center" direction="column">
						<AppIcon icon="check" size="48px" />
						<AppText textAlign="center" margin="1rem 0" weight="medium" size="l">
							{t("salaryBatch.processingModal.processed")}
						</AppText>
					</Flex>
				)}

				{createdEmployees.length > 0 && (
					<AppText
						margin="1rem 0 0.5rem 0"
						cursor="pointer"
						onClick={() => setShowCreatedCustomers(!showCreatedEmployees)}
					>
						{t("salaryBatch.processingModal.createdEmployees", {
							count: createdEmployees.length,
						})}
					</AppText>
				)}

				{showCreatedEmployees && (
					<div className="log">
						{createdEmployees.map((row) => (
							<AppText margin="0.4rem 0" size="s" color="grayTextDark" key={row.id}>
								{row.name} ({row.employeeNumber})
							</AppText>
						))}
					</div>
				)}

				{updatedEmployees.length > 0 && (
					<AppText
						margin="1rem 0 0.5rem 0"
						cursor="pointer"
						onClick={() => setShowUpdatedCustomers(!showUpdatedCustomers)}
					>
						{t("salaryBatch.processingModal.updatedEmployees", {
							count: updatedEmployees.length,
						})}
					</AppText>
				)}

				{showUpdatedCustomers && (
					<div className="log">
						{updatedEmployees.map((row) => (
							<AppText
								margin="0.5rem 0"
								size="s"
								color="grayTextDark"
								key={row.id}
								onClick={() => showDiff(row.update, row.prev)}
							>
								{row.name} ({row.employeeNumber})
							</AppText>
						))}
					</div>
				)}

				{processingWarnings.length > 0 && (
					<AppText
						margin="1rem 0 0.5rem 0"
						cursor="pointer"
						onClick={() => setShowProcessingWarnings(!showProcessingWarnings)}
					>
						{t("salaryBatch.processingModal.processingWarnings", {
							count: processingWarnings.length,
						})}
					</AppText>
				)}

				{showProcessingWarnings && (
					<div className="log">
						{processingWarnings.map((row) => (
							<AppText margin="0.4rem 0" size="s" color="grayTextDark" key={row}>
								{row}
							</AppText>
						))}
					</div>
				)}

				{processingLog.length > 0 && (
					<AppText margin="1rem 0" cursor="pointer" onClick={() => setShowLog(!showLog)}>
						{t("salaryBatch.processingModal.log", { count: processingLog.length })}
					</AppText>
				)}

				{showLog && (
					<div className="log">
						{processingLog.map((row, i) => (
							<AppText margin="0.4rem 0" size="s" color="grayTextDark" key={i}>
								{row}
							</AppText>
						))}
					</div>
				)}

				{showCloseBtn && (
					<Flex justifyContent="center">
						<Button width="130px" margin="1rem 0 0 0" onClick={onClose}>
							{t("CLOSE")}
						</Button>
					</Flex>
				)}
			</Styles>
		</Modal>
	);
};

export default SalaryBatchProcessingModal;

const Styles = styled.div`
	.log {
		max-height: 35vh;
		overflow: auto;
		white-space: nowrap;
	}
`;
